import {
    DefaultChatTransport,
    lastAssistantMessageIsCompleteWithApprovalResponses,
} from 'ai'
import type { FileUIPart } from 'ai'
import {
    computed,
    nextTick,
    onScopeDispose,
    reactive,
    ref,
    shallowRef,
    watch,
    watchEffect,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { useDocumentVisibility, usePreferredDark } from '@vueuse/core'
import { generateId } from 'ai'
import { useChat } from '@ai-sdk/vue'
import { defineStore } from 'pinia'
import {
    useSafeLocalStorage,
    useSafeSessionStorage,
} from '../useSafeLocalStorage'
import {
    isHostToolPart,
    isReservedHostToolName,
    isUserAnsweredTool,
    PAGE_TOOL_FEATURES,
    PageAccessLevel,
    pageAccessAllows,
    TOOL_TERMINAL_STATES,
} from 'models'
import type {
    Chat as ChatRecord,
    AgentFileUpload,
    AgentInterface,
    AgentModel,
    AgentVoiceCapability,
    AgentGatewayOptions,
    ChatMessageActions,
    MessageFeedback,
    RevisedAnswer,
    UIChatMessage,
    UIChatMessageMetadata,
    AgentTools,
    HostToolResult,
    HostToolsCapability,
    PageAccessCapability,
    PagePointer,
} from 'models'
import {
    PAGE_NAVIGATION_KEY,
    createHostToolRunner,
    declaredHostTools,
    isHostToolCall,
    type HostToolCall,
    type HostToolConfirmation,
    type RegisteredHostTool,
} from './hostTools'
import {
    collectDeviceContext,
    collectGeolocation,
} from 'utils/src/device-context'
import { logger } from 'utils/src/logger'
import {
    getTextPart,
    withAudioTranscripts,
    type AudioTranscriptUpdate,
} from 'utils'
import {
    CONVERSATIONS_PAGE_SIZE,
    createChatbotApiClient,
    type ChatbotFile,
} from './api'
import type { PendingAttachment } from './types'

// The store exposes `chat` (a reactive view of `useChat`) whose inferred
// type transitively references JSONObject. Re-export it so consumers can emit
// portable declarations that name the type (TS2883) without an unused import.
// This only works while the whole workspace resolves ONE @ai-sdk/provider: the
// declaration emitter needs the name to point at the very file the type came
// from, so the peer ranges on `ai`, `@ai-sdk/vue` and `@ai-sdk/provider` here
// and in `components` are pinned to the same versions the root installs.
export type { JSONObject } from '@ai-sdk/provider'

/**
 * Id prefix of the spoken QUESTIONS the client shows before the server has
 * them. Their own id space is what lets a reload replace them instead of
 * duplicating them (see {@link unpersistedVoiceLines}). What the agent says
 * never gets one: a spoken answer reaches the screen only as the written turn
 * the server persisted, whole, with its sources and its widgets.
 */
export const VOICE_LINE_ID_PREFIX = 'voice-'

/**
 * How long a call's page action that may unload the page waits after its
 * result left for the provider: the socket sends it, then the page may go.
 */
const VOICE_RESULT_SEND_MS = 300

/** How long the turn has to stay quiet before `isWorking` goes off. */
export const WORKING_SETTLE_MS = 400

/** A tool call of the answer waits for the user's approval card. */
const awaitsApproval = (message: UIChatMessage | undefined): boolean =>
    message?.role === 'assistant' &&
    message.parts.some(
        (part) => (part as { state?: string }).state === 'approval-requested',
    )

/** Whitespace-collapsed lowercase text, for comparing two transcripts. */
const normalizeSpoken = (text: string): string =>
    text.replace(/\s+/g, ' ').trim().toLowerCase()

/** Text of a message, whitespace-collapsed, for comparing two transcripts. */
const spokenText = (message: UIChatMessage): string =>
    normalizeSpoken(
        message.parts
            .filter((part) => part.type === 'text')
            .map((part) => ('text' in part ? part.text : ''))
            .join(' '),
    )

/**
 * The spoken questions the reload would silently drop.
 *
 * A question is shown the moment the provider transcribes it, while the server
 * writes it only when the turn it belongs to has been answered. So between the
 * two there is always a window, and replacing the list with the persisted one
 * wholesale made the caller's own words vanish from the screen while they were
 * still speaking (reported 2026-08-06).
 *
 * A line counts as persisted when a server message says the same thing, or
 * ENDS with it: the provider can prepend its own lead-in to what it heard, so
 * the spoken line is the persisted one with a prefix. Nothing looser than
 * that: matching on "contains" either way round let a short persisted turn
 * ("no", "sì") swallow a whole question the server did not have yet, which is
 * the disappearance this was supposed to fix.
 *
 * Only questions are considered because only questions are shown: what the
 * agent says arrives as the written turn and nothing else (see
 * {@link VOICE_LINE_ID_PREFIX}).
 */
const SHORTEST_MATCHABLE_LINE = 8

const unpersistedVoiceLines = (
    persisted: UIChatMessage[],
    shown: UIChatMessage[],
): UIChatMessage[] => {
    // Consumed as they match, one persisted line for one spoken line. With a
    // plain `some` a single persisted "sì" accounted for every later "sì" the
    // caller said, and the new one vanished from the screen before the server
    // had it. And a persisted question already on screen under its own id
    // accounts for itself: left in, the "sì" written two turns ago took the
    // "sì" just said for its own, which vanished the same way and made the
    // reload report its turn as written before it was.
    const onScreen = new Set(shown.map((message) => message.id))
    const server = persisted
        .filter(
            (message) => message.role === 'user' && !onScreen.has(message.id),
        )
        .map((message) => ({ text: spokenText(message), taken: false }))
    return shown.filter((message) => {
        if (
            !message.id.startsWith(VOICE_LINE_ID_PREFIX) ||
            message.role !== 'user'
        ) {
            return false
        }
        const text = spokenText(message)
        const match = server.find(
            (other) =>
                !other.taken &&
                other.text.length > 0 &&
                (other.text === text ||
                    (other.text.length >= SHORTEST_MATCHABLE_LINE &&
                        text.endsWith(other.text))),
        )
        if (!match) {
            return true
        }
        match.taken = true
        return false
    })
}

/**
 * How long a chat opened mid-turn waits for the turn to be written, and how
 * often it reads the chat meanwhile. Longer than the minute and a half after
 * which the server stops reporting the turn of a replica that is gone.
 */
const FOLLOW_TURN_TIMEOUT_MS = 120_000
const FOLLOW_TURN_RETRY_MS = 1_000

/** How often the turns running in the background are read again. */
export const RUNNING_TURNS_POLL_MS = 5_000

/** The chats asked about at once, as many as the server reads. */
const RUNNING_TURNS_MAX_IDS = 100

/**
 * How long a chat left before its request reached the server (`submitted`)
 * waits to be reported running once: until then its absence is not the end
 * of its turn, and after it the turn never started.
 */
const BACKGROUND_START_GRACE_MS = 30_000

/**
 * How long a queued message waits for the turn before it to be written, and
 * how often it asks meanwhile: the write follows the end of the stream.
 */
const QUEUE_WRITE_WAIT_MS = 5_000
const QUEUE_WRITE_RETRY_MS = 250
/** One ask that takes longer counts as "still running", so the wait keeps its bound. */
const QUEUE_WRITE_ASK_MS = 1_000

/** Between the queued messages, sent together as one. */
const QUEUE_SEPARATOR = '\n\n'

/** Tool name of a tool invocation part, '' for any other part. */
const partToolName = (part: UIChatMessage['parts'][number]): string => {
    const type = (part as { type?: string }).type ?? ''
    if (type === 'dynamic-tool') {
        return (part as { toolName?: string }).toolName ?? ''
    }
    return type.startsWith('tool-') ? type.slice('tool-'.length) : ''
}

/** A tool of the answer waits for the user to answer it: a form, a choice. */
const awaitsToolAnswer = (message: UIChatMessage | undefined): boolean =>
    message?.role === 'assistant' &&
    message.parts.some(
        (part) =>
            isUserAnsweredTool(partToolName(part)) &&
            !(part as { providerExecuted?: boolean }).providerExecuted &&
            !TOOL_TERMINAL_STATES.has((part as { state?: string }).state ?? ''),
    )

/**
 * True when the last assistant step carries a client tool the user has
 * answered, and nothing left in it for the user to answer.
 *
 * Replaces the SDK's `lastAssistantMessageIsCompleteWithToolCalls`, which asks
 * for an output from EVERY tool part of the step. Most client tools never get
 * one: they render from the call input and there is nothing to send back (see
 * USER_ANSWERED_TOOLS). So a step that shows a form NEXT TO another widget
 * never satisfied that condition, the submitted answers stayed in the browser
 * and the turn was never resumed: the user filled the form and the agent went
 * silent. Reported on 2026-08-27 for showForm + showSources, the routine
 * pairing on a knowledge-base turn, where the sources closure is synthesized
 * server-side into the same step.
 *
 * At least one answered tool is required, so a turn ending on display-only
 * widgets still sends nothing.
 */
export const lastAssistantMessageIsCompleteWithAnsweredClientTools = ({
    messages,
}: {
    messages: UIChatMessage[]
}): boolean => {
    const message = messages[messages.length - 1]
    if (!message || message.role !== 'assistant') {
        return false
    }
    const lastStepStartIndex = message.parts.reduce(
        (last, part, index) => (part.type === 'step-start' ? index : last),
        -1,
    )
    const userAnsweredParts = message.parts
        .slice(lastStepStartIndex + 1)
        .filter(
            (part) =>
                !(part as { providerExecuted?: boolean }).providerExecuted &&
                // A host call is answered by the app that embeds the chat
                // (spec `host-tools.md`), and resumes the turn the same way.
                (isUserAnsweredTool(partToolName(part)) ||
                    isHostToolPart(part)),
        )
    return (
        userAnsweredParts.length > 0 &&
        userAnsweredParts.every((part) =>
            TOOL_TERMINAL_STATES.has((part as { state?: string }).state ?? ''),
        )
    )
}

type ChatbotView = 'chat' | 'conversations' | 'profile' | 'auth' | 'files'

export function useChatbotStore(agentId: string) {
    return defineStore(`chatbot-${agentId}`, () => {
        const { t, te, locale } = useI18n({ useScope: 'global' })

        // === Configuration state ===
        const baseUrl = ref('')
        const basePath = ref<string | undefined>()
        const agentIdRef = ref(agentId)
        const name = ref<string | undefined>()
        const agentType = ref<'chat' | 'reasoning' | undefined>()
        const userId = ref<string | undefined>()
        const agentInterface = ref<AgentInterface | undefined>()
        const agentModel = ref<AgentModel | undefined>()
        const agentTools = ref<AgentTools | undefined>()
        const headers = ref<Record<string, string> | undefined>()
        const revisedAnswers = ref<RevisedAnswer[] | undefined>()
        /** Compatibility with PkChatbot `model` prop — overrides agentModel.model when set */
        const model = ref<string | undefined>()
        /** Raw actions from parent prop, filtered by agentType into `actions` */
        const actionsInput = ref<ChatMessageActions[] | undefined>()
        /** File upload configuration from agent settings */
        const agentFileUpload = ref<AgentFileUpload | undefined>()
        /** Voice configuration from agent settings: absent means no voice. */
        const agentVoice = ref<AgentVoiceCapability | undefined>()
        /** Fallback models for AI Gateway routing */
        const fallbackModels = ref<string[] | undefined>()
        /** Gateway options (ZDR, provider timeouts) */
        const gatewayOptions = ref<Partial<AgentGatewayOptions> | undefined>()
        /** External context set by the host application (e.g. via setContext() on the custom element). Validated server-side against the agent's externalContextSchema. */
        const externalContext = ref<Record<string, unknown> | undefined>()
        /** Host tools as the agent allows them, from the public agent: absent is off. */
        const agentHostTools = ref<HostToolsCapability | undefined>()
        /** Tools the host registered (`registerTool` on the custom element). Shallow: they carry functions. */
        const hostToolRegistry = shallowRef<RegisteredHostTool[] | undefined>()
        /** Host calls queued or running: the turn is not over while there are any. */
        const hostToolsPending = ref(0)
        /** Page calls of a voice call queued or running: the page control bar stays up for them. */
        const voicePageCallsPending = ref(0)
        /** Page access as the agent allows it (spec `page-tools.md`): absent is off. */
        const agentPageAccess = ref<PageAccessCapability | undefined>()
        /** The page tools of the widget: never declared as host tools, run by the same queue. */
        const pageToolRegistry = shallowRef<RegisteredHostTool[] | undefined>()
        /** The tools of the forms annotated on the page, read again each time they are needed. */
        const pageFormsProvider = shallowRef<
            (() => RegisteredHostTool[]) | undefined
        >()
        /** The page action running, what the page control bar shows; undefined when none. */
        const pageAction = ref<{ title: string } | undefined>()
        /**
         * What the user pointed at on the page for the next message (spec
         * `page-point-and-choose.md`, P): the chip above the input, sent on
         * the message's metadata.
         */
        const pagePointer = ref<PagePointer | undefined>()
        /** Starts pointing at the page: the widget's, absent where the chat cannot read a page. */
        const pagePointerStart = shallowRef<(() => void) | undefined>()
        /** The user is choosing an element of the page now. */
        const pagePointing = ref(false)
        /**
         * The titles of this page's steps by call, naming what each acts on
         * (spec `page-tools-improvements.md`, M8). Not saved with the chat:
         * after a reload the steps show their tool's own title.
         */
        const hostStepTitles = ref<Record<string, string>>({})
        /** True from the first page action of a response until the response ends. */
        const pageControlActive = ref(false)
        /** Requests started: a deferred click waits for the next one to stream. */
        const requestCount = ref(0)
        /** System-level theme override passed by the host application */
        const systemTheme = ref<'light' | 'dark' | undefined>()
        /** Hide the synthetic welcome message built from `initialMessage` (the fullscreen layout shows a greeting in its place) */
        const hideWelcomeMessage = ref(false)

        const prefersDark = usePreferredDark()
        const isDark = computed(() => {
            let theme = agentInterface.value?.theme
            if (!theme || theme === 'auto') {
                theme = systemTheme.value
            }
            if (!theme) {
                return prefersDark.value
            }
            return theme === 'dark'
        })

        const isReasoningAgent = computed(() => agentType.value === 'reasoning')

        const actions = computed<ChatMessageActions[] | undefined>(() => {
            if (!isReasoningAgent.value) {
                return actionsInput.value
            }
            return actionsInput.value?.filter(
                (action) =>
                    !['upvote', 'downvote', 'feedback'].includes(action),
            )
        })

        // === LocalStorage chatId ===
        const storageId = useSafeLocalStorage<string | null>(
            computed(() => `${agentIdRef.value}-chatId`),
            null,
        )

        watchEffect(() => {
            if (!storageId.value) {
                storageId.value = generateId()
            }
        })

        const localChatId = computed({
            get: () => storageId.value!,
            set: (value: string) => {
                storageId.value = value
            },
        })

        // === Page access switched off by the user (spec `page-tools.md`) ===
        // For this agent in this tab: sessionStorage is the tab's and the
        // site's origin's, the scope the user decided for.
        const pageAccessOff = useSafeSessionStorage<boolean>(
            computed(() => `${agentIdRef.value}-pageAccessOff`),
            false,
        )
        /** The page tools the requests declare and the queue runs: none once the user switched them off. */
        const activePageTools = computed(() =>
            pageAccessOff.value ? [] : (pageToolRegistry.value ?? []),
        )
        /** The annotated forms of the page now: none once the user switched page access off. */
        const pageForms = () =>
            pageAccessOff.value ? [] : (pageFormsProvider.value?.() ?? [])
        /** The agent may use this page: the menu then offers to switch it off, or on again. */
        const pageAccessAvailable = computed(() =>
            Boolean(agentPageAccess.value && pageToolRegistry.value?.length),
        )
        /** The user may point at the page: the agent offers it, the widget can, and the page tools are on. */
        const pagePointerAvailable = computed(
            () =>
                !!pagePointerStart.value &&
                agentPageAccess.value?.pointer === true &&
                activePageTools.value.length > 0,
        )
        // Switched off, the page sends nothing more: not what was pointed at
        watch(pagePointerAvailable, (available) => {
            if (!available) {
                pagePointer.value = undefined
            }
        })

        // === Page access notice (spec `page-tools.md`) ===
        // Closed for this agent in this browser, up to the level it spoke of:
        // a level raised later is news, and the notice comes back; a lower
        // one is part of what the user already read.
        const pageNoticeClosedAt = useSafeLocalStorage<string | null>(
            computed(() => `${agentIdRef.value}-pageNotice`),
            null,
        )
        /** The level the notice above the input speaks of; undefined when it is not shown. */
        const pageNoticeLevel = computed(() => {
            const level = agentPageAccess.value?.level
            return level &&
                level !== PageAccessLevel.Off &&
                activePageTools.value.length &&
                !pageAccessAllows(
                    (pageNoticeClosedAt.value ?? undefined) as
                        PageAccessLevel | undefined,
                    level,
                )
                ? level
                : undefined
        })
        function closePageNotice() {
            pageNoticeClosedAt.value = agentPageAccess.value?.level ?? null
        }

        // === Chat error ===
        const chatError = ref<Error | undefined>()
        const isConversationBlocked = ref(false)

        const isConversationBlockedError = computed(
            () =>
                chatError.value?.message
                    ?.toLowerCase()
                    .includes('conversation-blocked') ?? false,
        )

        function isEntityNotFoundError(error: Error): boolean {
            try {
                const parsed = JSON.parse(error.message)
                return (
                    parsed.status === 404 ||
                    parsed.type?.includes('entity-not-found')
                )
            } catch {
                return false
            }
        }

        /**
         * When an anonymous session expires and rotates, the server returns a
         * 404 (entity-not-found) because the existing chatId belongs to the
         * previous userId. In that case we transparently generate a new chatId
         * and re-send the last user message so the conversation restarts
         * without the user noticing.
         */
        function handleChatError(error: Error) {
            if (isEntityNotFoundError(error)) {
                const pendingMessages = chat.value.messages.filter(
                    (m) => m.role === 'user',
                )
                const lastUserMessage =
                    pendingMessages[pendingMessages.length - 1]
                const lastUserText = lastUserMessage
                    ? getTextPart(lastUserMessage)
                    : undefined

                localChatId.value = generateId()
                chatError.value = undefined

                // The chat of the new id exists from the next tick, when
                // `useChat` recreates it: resend there, not to the one that
                // just failed with the old id.
                void nextTick(() => {
                    chat.value.clearError()
                    if (lastUserText) {
                        chat.value.sendMessage({ text: lastUserText })
                    }
                })
                return
            }

            logger.error(error)
            chatError.value = error
            if (isConversationBlockedError.value) {
                isConversationBlocked.value = true
            }
        }

        // === API client ===
        const apiClient = computed(() =>
            createChatbotApiClient({
                baseUrl: baseUrl.value,
                basePath: basePath.value,
                headers: () => headers.value,
            }),
        )

        // === Chat streaming instance ===
        const storedGeolocation = ref<{
            geolocationLatitude: number
            geolocationLongitude: number
            geolocationAccuracy?: number
            geolocationCity?: string
        } | null>(null)

        /**
         * The device this conversation is happening on, plus the position if
         * the user has shared it. Sent with every typed turn and, once, when a
         * call opens: a spoken turn reaches the backend from the voice provider
         * and can carry nothing of its own.
         */
        const buildDeviceContext = () => ({
            ...collectDeviceContext(),
            ...(storedGeolocation.value ?? {}),
        })

        /**
         * True once a request started after this call is streaming: the
         * backend merges the outputs it carries before it streams anything,
         * so the first chunk is the proof it holds them. False after 30 s.
         */
        const waitForResume = () => {
            const startedBefore = requestCount.value
            return new Promise<boolean>((resolve) => {
                const timer = setTimeout(() => {
                    stop()
                    resolve(false)
                }, 30_000)
                const stop = watch(
                    () => [requestCount.value, chat.value.status] as const,
                    ([count, status]) => {
                        if (count > startedBefore && status === 'streaming') {
                            clearTimeout(timer)
                            stop()
                            resolve(true)
                        }
                    },
                )
            })
        }

        /**
         * The host call waiting for the user's consent: what the confirmation
         * card shows. Kept here only, never in a message or a request.
         */
        const hostConfirmation = shallowRef<
            | {
                  toolCallId: string
                  question?: string
                  details?: HostToolConfirmation['details']
                  unchanged?: HostToolConfirmation['unchanged']
                  answer: (approved: boolean) => void
              }
            | undefined
        >()
        const askHostConfirmation = (
            call: HostToolCall,
            confirmation?: HostToolConfirmation,
        ) =>
            new Promise<boolean>((resolve) => {
                hostConfirmation.value?.answer(false)
                hostConfirmation.value = {
                    toolCallId: call.toolCallId,
                    question: confirmation?.question,
                    details: confirmation?.details,
                    unchanged: confirmation?.unchanged,
                    answer: (approved) => {
                        hostConfirmation.value = undefined
                        resolve(approved)
                    },
                }
            })
        /** The user's answer on the confirmation card. */
        function respondHostConfirmation(approved: boolean) {
            hostConfirmation.value?.answer(approved)
        }

        /**
         * What a request declares of the page tools (spec `page-tools.md`),
         * nothing when they are off. A call declares none of the forms: the
         * provider takes its tools before the call, and a form's name is the
         * page's.
         */
        const declarePageTools = ({ forms }: { forms: boolean }) =>
            agentPageAccess.value && activePageTools.value.length
                ? {
                      version: 1 as const,
                      // What this bundle runs: the backend offers nothing past it
                      features: [...PAGE_TOOL_FEATURES],
                      // What the model reads of each form: never its `execute`.
                      forms: forms
                          ? pageForms().map(
                                ({
                                    name,
                                    description,
                                    inputSchema,
                                    annotations,
                                }) => ({
                                    name,
                                    description,
                                    inputSchema,
                                    annotations,
                                }),
                            )
                          : [],
                  }
                : undefined

        /** What the page control bar and the steps show of the call starting, or of none. */
        const showPageActivity = (call: HostToolCall | undefined) => {
            if (call?.provider !== 'page') {
                pageAction.value = undefined
                return
            }
            const tool = [...activePageTools.value, ...pageForms()].find(
                (candidate) => candidate.name === call.toolName,
            )
            // What the step acts on, by name; a failure keeps the
            // tool's own title
            let described: string | undefined
            try {
                described = tool?.describe?.(call.input)
            } catch {
                described = undefined
            }
            if (described) {
                hostStepTitles.value = {
                    ...hostStepTitles.value,
                    [call.toolCallId]: described,
                }
            }
            if (call.readOnly) {
                pageAction.value = undefined
                return
            }
            pageAction.value = {
                title: described ?? tool?.annotations?.title ?? call.toolName,
            }
            pageControlActive.value = true
        }

        const hostToolRunner = createHostToolRunner({
            registry: () => [
                // Never a page's tool under a reserved name: the backend
                // offers only the widget's own under it, and the page's
                // would run in its place without the widget's confirmation.
                ...(hostToolRegistry.value ?? []).filter(
                    (tool) => !isReservedHostToolName(tool.name),
                ),
                ...activePageTools.value,
                ...pageForms(),
            ],
            capability: () => agentHostTools.value,
            addToolOutput: (output) =>
                void chat.value.addToolOutput(
                    output as Parameters<typeof chat.value.addToolOutput>[0],
                ),
            confirm: askHostConfirmation,
            waitForResume,
            onPendingChange: (pending) => {
                hostToolsPending.value = pending
            },
            onActivity: showPageActivity,
        })

        /**
         * The page calls of a voice call waiting for their result, by the id
         * the widget gave them: the provider awaits each one's promise, and
         * the runner settles it (spec `voice-page-control.md`).
         */
        const voicePageCalls = new Map<string, (result: string) => void>()
        let voicePageCallCount = 0

        /**
         * Answers the provider with the result as the backend reads a page
         * tool's: serialized whole, so a failure stays a failure (the
         * provider passes on the text, not its own error flag).
         */
        const settleVoicePageCall = (
            toolCallId: string,
            result: HostToolResult,
        ) => {
            const settle = voicePageCalls.get(toolCallId)
            voicePageCalls.delete(toolCallId)
            settle?.(JSON.stringify(result))
        }

        /**
         * The card of a call's page action, once the card is free: one
         * question at a time, and the chat's is never answered no on the
         * user's behalf. A call stopped while it waited asks nothing.
         */
        const askVoicePageConfirmation = async (
            call: HostToolCall,
            confirmation?: HostToolConfirmation,
        ) => {
            while (hostConfirmation.value) {
                await new Promise<void>((resolve) => {
                    const stop = watch(hostConfirmation, (current) => {
                        if (!current) {
                            stop()
                            resolve()
                        }
                    })
                })
            }
            if (!voicePageCalls.has(call.toolCallId)) {
                return false
            }
            return askHostConfirmation(call, confirmation)
        }

        /**
         * Runs the page calls of a voice call: the same tools, confirmation
         * card and presence as a typed turn's, with the provider in place of
         * the resume. Its own queue, so a call's actions never wait behind
         * the chat's, nor are cancelled by a message typed meanwhile.
         */
        const voicePageToolRunner = createHostToolRunner({
            registry: () => activePageTools.value,
            capability: () => agentHostTools.value,
            addToolOutput: (output) =>
                settleVoicePageCall(
                    output.toolCallId,
                    'output' in output
                        ? output.output
                        : {
                              content: [
                                  { type: 'text', text: output.errorText },
                              ],
                              isError: true,
                          },
                ),
            confirm: askVoicePageConfirmation,
            // No resume to wait for: the provider sends the result on the
            // call's socket. A beat for that send before an action that may
            // unload the page.
            waitForResume: () =>
                new Promise((resolve) =>
                    setTimeout(() => resolve(true), VOICE_RESULT_SEND_MS),
                ),
            onPendingChange: (pending) => {
                voicePageCallsPending.value = pending
            },
            onActivity: showPageActivity,
        })

        /**
         * The page tools of a call opened from this widget: their declaration
         * for the session request, and the handlers the voice provider calls
         * with each call's arguments. Nothing when the page tools are off.
         */
        function voicePageTools():
            | {
                  declaration: NonNullable<ReturnType<typeof declarePageTools>>
                  clientTools: Record<
                      string,
                      (parameters: unknown) => Promise<string>
                  >
              }
            | undefined {
            const declaration = declarePageTools({ forms: false })
            if (!declaration) {
                return undefined
            }
            return {
                declaration,
                clientTools: Object.fromEntries(
                    activePageTools.value.map((tool) => [
                        tool.name,
                        (parameters: unknown) =>
                            new Promise<string>((resolve) => {
                                voicePageCallCount += 1
                                // The provider hands the handler no call id
                                const toolCallId = `page-call-${voicePageCallCount}`
                                voicePageCalls.set(toolCallId, resolve)
                                voicePageToolRunner.enqueue({
                                    toolCallId,
                                    toolName: tool.name,
                                    input: parameters ?? {},
                                    messageId: lastChatMessage()?.id ?? '',
                                    readOnly:
                                        tool.annotations?.readOnlyHint === true,
                                    provider: 'page',
                                })
                            }),
                    ]),
                ),
            }
        }

        /**
         * Ends the page calls of a voice call: on the Stop of the control
         * bar, and when the call ends. The provider still waits on each one,
         * so each gets an answer: a failure the model reads as such.
         */
        function cancelVoicePageCalls() {
            voicePageToolRunner.cancelAll()
            // Its card goes too; the chat's own stays.
            if (
                hostConfirmation.value &&
                voicePageCalls.has(hostConfirmation.value.toolCallId)
            ) {
                hostConfirmation.value.answer(false)
            }
            for (const toolCallId of [...voicePageCalls.keys()]) {
                settleVoicePageCall(toolCallId, {
                    content: [{ type: 'text', text: 'Stopped by the user.' }],
                    isError: true,
                })
            }
        }

        /**
         * The page control bar's stop (spec `page-tools.md`). Unlike
         * `stopGeneration` it keeps the conversation as it is: the actions
         * already done on the page stay done, and the answer that did them
         * must stay in view.
         */
        async function stopHostActions() {
            cancelHostCalls()
            cancelVoicePageCalls()
            pageAction.value = undefined
            pageControlActive.value = false
            lastSubmittedInput.value = ''
            await chat.value.stop()
        }

        /**
         * Cancels the host calls queued and running, and the consent they wait
         * for: the card goes, and its call ends without an output. The card
         * of a call's page action stays: the call goes on (see
         * `cancelVoicePageCalls`).
         */
        function cancelHostCalls() {
            hostToolRunner.cancelAll()
            if (
                hostConfirmation.value &&
                !voicePageCalls.has(hostConfirmation.value.toolCallId)
            ) {
                hostConfirmation.value.answer(false)
            }
        }

        /** The user's switch from the widget menu: off, it stops what runs first. */
        async function setPageAccessOff(off: boolean) {
            pageAccessOff.value = off
            if (off) {
                await stopHostActions()
            }
        }

        // A call of the conversation left behind has nowhere to land. Sync,
        // and registered before the watcher that detaches the chat: a result
        // landing in between would resume the turn on the chat being left.
        watch(localChatId, () => cancelHostCalls(), { flush: 'sync' })

        /** A resume is running: its errors are not the user's, see `followTurn`. */
        let isResumingTurn = false
        /** The turn `followTurn` is following, until it ends or is stopped. */
        let followedTurn: { chatId: string } | undefined
        /** The chat of `followedTurn`, for what the screen shows of it. */
        const followedChatId = ref<string | undefined>()
        /**
         * The name of the turn on screen, from its stream: its Stop sends it,
         * since a turn sent from another page may have replaced it as the
         * chat's running one.
         */
        let turnStreamId: string | undefined

        // === Turns left running (spec resumable-chat-streams, F2) ===
        /** The chats whose turn runs, as the server last said. */
        const serverRunningChatIds = ref<string[]>([])
        /** Chats left while their turn ran: their end shows a notice. */
        const backgroundTurns = ref<string[]>([])
        /** Turns that ended in the background, one notice each until opened or closed. */
        const answeredInBackground = ref<string[]>([])
        /**
         * The question of each chat left running: the name of its row while
         * the list does not have it yet (the list leaves out a chat until its
         * first turn is written) and of its notice while it has no title.
         */
        const backgroundQuestions = ref<Record<string, string>>({})
        /** When each chat was left, for the grace of a turn not started yet. */
        const backgroundLeftAt = new Map<string, number>()
        /** Chats whose turn the server has reported running at least once. */
        const seenRunning = new Set<string>()

        function noteBackgroundTurn(
            chatId: string,
            question: string,
            started: boolean,
        ) {
            if (!backgroundTurns.value.includes(chatId)) {
                backgroundTurns.value = [...backgroundTurns.value, chatId]
            }
            backgroundLeftAt.set(chatId, Date.now())
            // A turn streaming has its record already; one still `submitted`
            // may not, and is over only once the server has reported it.
            if (started) {
                seenRunning.add(chatId)
            } else {
                seenRunning.delete(chatId)
            }
            if (question) {
                backgroundQuestions.value = {
                    ...backgroundQuestions.value,
                    [chatId]: question,
                }
            }
            if (!runningTurnsTimer) {
                scheduleRunningTurns()
            }
        }

        function forgetBackgroundQuestion(chatId: string) {
            backgroundQuestions.value = Object.fromEntries(
                Object.entries(backgroundQuestions.value).filter(
                    ([id]) => id !== chatId,
                ),
            )
        }

        /** Back on the chat: its turn, if still running, is followed here. */
        function forgetBackgroundTurn(chatId: string) {
            backgroundLeftAt.delete(chatId)
            backgroundTurns.value = backgroundTurns.value.filter(
                (id) => id !== chatId,
            )
            answeredInBackground.value = answeredInBackground.value.filter(
                (id) => id !== chatId,
            )
            forgetBackgroundQuestion(chatId)
        }

        // `useChat` recreates the chat, empty, whenever what this getter reads
        // synchronously changes: the chatId, the endpoint, the agent type.
        // The recreation runs on the next tick, not at the change: code that
        // rotates the chatId waits for it before acting on the new chat.
        const chatHelpers = useChat<UIChatMessage>(() => ({
            id: localChatId.value,
            transport: new DefaultChatTransport({
                api: apiClient.value.getChatEndpoint(
                    agentIdRef.value,
                    isReasoningAgent.value ? 'reasoning' : 'chat',
                ),
                // Read when the chat resumes, not here: see `followTurn`.
                prepareReconnectToStreamRequest: ({ id }) => ({
                    api: apiClient.value.getChatStreamEndpoint(
                        agentIdRef.value,
                        id,
                    ),
                }),
                prepareSendMessagesRequest: ({ messages: msgs, id }) => ({
                    body: {
                        id,
                        messages: [msgs[msgs.length - 1]],
                        model: model.value,
                        prompt: agentModel.value?.prompt,
                        maxOutputTokens: agentModel.value?.maxOutputTokens,
                        temperature: agentModel.value?.temperature,
                        presencePenalty: agentModel.value?.presencePenalty,
                        frequencyPenalty: agentModel.value?.frequencyPenalty,
                        searchMode: agentModel.value?.searchMode,
                        limit: agentModel.value?.limit,
                        reasoning: agentModel.value?.reasoning,
                        webSearch: agentTools.value?.webSearch,
                        smoothStream: agentInterface.value?.smoothStream,
                        deviceContext: buildDeviceContext(),
                        externalContext: externalContext.value,
                        disabledTools: agentTools.value?.disabled,
                        fallbackModels: fallbackModels.value,
                        gatewayOptions: gatewayOptions.value,
                        hostTools: declaredHostTools(
                            hostToolRegistry.value,
                            agentHostTools.value,
                        ),
                        pageTools: declarePageTools({ forms: true }),
                    },
                }),
                credentials: 'include',
                // Resolved per-request: reading `headers.value` here
                // would make `useChat` recreate the chat (wiping its
                // messages) on every auth token rotation.
                headers: () => headers.value ?? {},
            }),
            // Two resume triggers: a client tool the user answered,
            // and a tool approval the user just answered. Without the
            // second one the approval response would sit in the
            // browser and the suspended call would never run.
            sendAutomaticallyWhen: (options) =>
                lastAssistantMessageIsCompleteWithAnsweredClientTools(
                    options,
                ) ||
                lastAssistantMessageIsCompleteWithApprovalResponses(options),
            onError: (error) => {
                // A resume that fails is not the user's error: the
                // reload that follows it shows what the chat stores.
                if (isResumingTurn) {
                    logger.warn(error)
                    return
                }
                handleChatError(error)
            },
            // A host call goes to the queue and returns at once: the
            // SDK awaits this callback before reading the rest of the
            // stream (see createHostToolRunner).
            onToolCall: ({ toolCall }) => {
                // A resume replays the turn into a message of its own,
                // which answering would send back over the stored one:
                // the stored answer runs its open page reads afterwards
                // (see `continueAfterLoad`).
                if (!isHostToolCall(toolCall) || isResumingTurn) {
                    return
                }
                const call = toolCall as unknown as {
                    toolCallId: string
                    toolName: string
                    input: unknown
                    toolMetadata?: {
                        readOnly?: unknown
                        provider?: unknown
                    }
                }
                hostToolRunner.enqueue({
                    toolCallId: call.toolCallId,
                    toolName: call.toolName,
                    input: call.input,
                    messageId: lastChatMessage()?.id ?? '',
                    readOnly: call.toolMetadata?.readOnly === true,
                    provider:
                        call.toolMetadata?.provider === 'page' ? 'page' : 'app',
                })
            },
            // The transcripts of the audio just sent, streamed before
            // the answer: the stored message has them, this copy of
            // it gets them here (spec `audio-file-transcription.md`).
            onData: (part) => {
                if (part.type === 'data-turnStream') {
                    turnStreamId = (part.data as { streamId: string }).streamId
                    return
                }
                if (part.type === 'data-audioTranscripts') {
                    chat.value.messages = withAudioTranscripts(
                        chat.value.messages,
                        part.data as AudioTranscriptUpdate[],
                    )
                }
            },
            // Chat UI init keeps `onFinish` in v7 (the onEnd rename
            // only applies to generateText/streamText callbacks).
            onFinish: () => {
                // A new chat is persisted on the backend only after the
                // first message: reload the list if it's not there yet.
                const id = localChatId.value
                if (
                    !isReasoningAgent.value &&
                    !conversations.value.some((c) => c.id === id)
                ) {
                    loadConversations(agentIdRef.value)
                }
            },
        }))
        /**
         * The chat as the store, its components and its tests read it
         * (`chat.value.messages`, `chat.value.status`, `store.chat.messages =`),
         * the way they read the deprecated `Chat` instance it replaces:
         * `reactive` unwraps the helpers' refs, and an assignment to `messages`
         * reaches the chat through them.
         */
        const chat = computed(() => reactive(chatHelpers))
        /** What the `Chat` instance gave as `lastMessage`, which `useChat` does not expose. */
        const lastChatMessage = (): UIChatMessage | undefined =>
            chat.value.messages[chat.value.messages.length - 1]

        // Leaving a chat (another conversation, a new chat, a rotated
        // session) detaches the browser from its turn, which goes on
        // server-side and is resumed when the chat is opened again: a stream
        // left reading would keep calling `onError` and `onData` on the chat
        // on screen. Sync, so it reaches the chat being left before `useChat`
        // replaces it on the next tick. A turn left running is noted, for the
        // notice its end shows wherever the user is then.
        watch(
            localChatId,
            (chatId, leftChatId) => {
                if (
                    leftChatId &&
                    !isReasoningAgent.value &&
                    (isTurnInFlight() || followedTurn?.chatId === leftChatId)
                ) {
                    const questions = chat.value.messages.filter(
                        (message) => message.role === 'user',
                    )
                    const question = questions[questions.length - 1]
                    noteBackgroundTurn(
                        leftChatId,
                        question ? (getTextPart(question) ?? '') : '',
                        chat.value.status === 'streaming' ||
                            followedTurn?.chatId === leftChatId,
                    )
                }
                forgetBackgroundTurn(chatId)
                turnStreamId = undefined
                // The chat opened is not working: the quiet moment that keeps
                // the signal from blinking belongs to the chat left.
                clearTimeout(workingTimer)
                isWorking.value = false
                void chat.value.stop()
            },
            { flush: 'sync' },
        )

        // === Messages ===
        const initialMessageResolved = computed(() => {
            const val = agentInterface.value?.initialMessage
            if (!val?.[locale.value]) {
                return te('message.defaultAgentWelcome')
                    ? t('message.defaultAgentWelcome')
                    : ''
            }
            return val[locale.value]
        })

        // Stable across recomputations (locale or config changes), so keyed
        // renders don't remount the welcome bubble and feedback stays attached.
        const welcomeMessageId = generateId()
        const welcomeMessage = computed<UIChatMessage>(() => ({
            id: welcomeMessageId,
            role: 'assistant',
            parts: [{ type: 'text', text: initialMessageResolved.value }],
        }))

        const messages = computed(() => {
            // Resolve any remaining `attachment://` URLs that haven't been
            // resolved server-side yet (e.g. in-flight streaming messages).
            const resolveInFlight = (msgs: UIChatMessage[]) =>
                msgs.map((msg) => {
                    if (msg.role !== 'user') {
                        return msg
                    }
                    const hasFileParts = msg.parts.some(
                        (p) => p.type === 'file',
                    )
                    if (!hasFileParts) {
                        return msg
                    }
                    return {
                        ...msg,
                        parts: msg.parts.map((p) => {
                            if (p.type !== 'file') {
                                return p
                            }
                            const fp = p as FileUIPart
                            if (fp.url.startsWith('attachment://')) {
                                const storedFileName = fp.url.slice(
                                    'attachment://'.length,
                                )
                                return {
                                    ...fp,
                                    url: apiClient.value.getMediaUrl(
                                        agentIdRef.value,
                                        localChatId.value,
                                        storedFileName,
                                    ),
                                } as FileUIPart
                            }
                            // Blob URLs (from pending uploads) are already
                            // usable as-is — don't append auth tokens.
                            if (fp.url.startsWith('blob:')) {
                                return fp
                            }
                            return {
                                ...fp,
                                url: apiClient.value.appendAuthToken(fp.url),
                            } as FileUIPart
                        }),
                    }
                })

            // From a chatId change to the next tick the chat is still the
            // previous one (`useChat` recreates it then): show the new chat as
            // the empty one it is about to be, not the old conversation.
            const chatMessages =
                chat.value.id === localChatId.value ? chat.value.messages : []

            if (isReasoningAgent.value) {
                return resolveInFlight(chatMessages)
            }
            if (
                !hideWelcomeMessage.value &&
                chatMessages[0]?.role !== 'assistant'
            ) {
                return [welcomeMessage.value, ...resolveInFlight(chatMessages)]
            }
            return resolveInFlight(chatMessages)
        })

        // === Localized placeholder ===
        const inputMessagePlaceholder = computed(() => {
            const val = agentInterface.value?.messagePlaceholder
            if (!val?.[locale.value]) {
                return te('placeholder.askAnything')
                    ? t('placeholder.askAnything')
                    : ''
            }
            return val[locale.value]
        })

        // === Conversations ===
        const conversations = ref<ChatRecord[]>([])
        const isLoadingConversations = ref(false)
        // The list loads a page at a time: the last page loaded, whether a
        // full one says more may follow, the next one in flight, and whose
        // list the loaded pages are (a sign-in changes the user)
        const conversationsPage = ref(1)
        const hasMoreConversations = ref(false)
        const isLoadingMoreConversations = ref(false)
        let conversationsOwner: string | undefined
        const chatFiles = ref<ChatbotFile[]>([])
        const isLoadingChatFiles = ref(false)

        // === Navigation ===
        const activeView = ref<ChatbotView>('chat')
        // Bumped to send the focus to the message field from outside the
        // chat view (the host's ⇧Esc): the chat view watches it
        const inputFocusRequest = ref(0)

        // === Message feedback state ===
        const messageFeedbacks = ref<MessageFeedback[]>([])
        const feedbackDialogMessage = ref<UIChatMessage | undefined>()
        const isFeedbackSubmitting = ref(false)
        const isFeedbackSubmitted = ref(false)
        const feedbackSubmitError = ref<string | undefined>()

        // === Lead form state ===
        const isLeadSubmitted = ref(false)
        const isLoadingSubmitLead = ref(false)
        const submitLeadError = ref<string | undefined>()

        // === Input ===
        const input = ref('')
        const pendingAttachments = ref<PendingAttachment[]>([])
        const lastSubmittedInput = ref('')
        /** What the user pointed at with the message a Stop may roll back: it goes back with the text. */
        const lastSubmittedPointer = shallowRef<PagePointer | undefined>()
        const messageCountBeforeSubmit = ref(0)

        // === Queue: what the user writes while a turn runs (spec
        // resumable-chat-streams, F2) ===
        /** Per chat, in the tab: a reload keeps what was queued. */
        const queues = useSafeSessionStorage<Record<string, string[]>>(
            computed(() => `${agentIdRef.value}-queue`),
            {},
        )
        /** Queued in the chat on screen: sent together when its turn ends. */
        const queuedMessages = computed(
            () => queues.value[localChatId.value] ?? [],
        )
        /** A reasoning agent opens a new chat at every message: nothing waits. */
        const canQueue = computed(() => !isReasoningAgent.value)
        function setQueue(chatId: string, queued: string[]) {
            const others = Object.fromEntries(
                Object.entries(queues.value).filter(([id]) => id !== chatId),
            )
            queues.value =
                queued.length > 0 ? { ...others, [chatId]: queued } : others
        }
        /** Takes the chat's queue out, as the one message it is sent as. */
        function takeQueue(chatId: string): string {
            const text = (queues.value[chatId] ?? []).join(QUEUE_SEPARATOR)
            setQueue(chatId, [])
            return text
        }
        /** Puts text back in the input, after what the user is writing. */
        function restoreInput(text: string) {
            if (text) {
                input.value = input.value
                    ? `${input.value}${QUEUE_SEPARATOR}${text}`
                    : text
            }
        }
        function removeQueuedMessage(index: number) {
            setQueue(
                localChatId.value,
                queuedMessages.value.filter((_, i) => i !== index),
            )
        }
        /** Back to the input, to be changed and sent or queued again. */
        function editQueuedMessage(index: number) {
            const text = queuedMessages.value[index]
            removeQueuedMessage(index)
            restoreInput(text ?? '')
        }

        // === Internal loaders ===
        async function loadConversations(id: string) {
            if (isReasoningAgent.value) {
                conversations.value = []
                return
            }
            isLoadingConversations.value = true
            try {
                const listed = await apiClient.value.getConversations(
                    id,
                    userId.value,
                )
                // A chat left during its first turn is not listed until the
                // turn is written: its row stands in meanwhile, named by its
                // question, so the list can mark it.
                const listedIds = new Set(listed.map((c) => c.id))
                const leftRunning = backgroundTurns.value
                    .filter(
                        (chatId) =>
                            !listedIds.has(chatId) &&
                            backgroundQuestions.value[chatId],
                    )
                    .map(
                        (chatId) =>
                            ({
                                id: chatId,
                                agentId: id,
                                title: backgroundQuestions.value[chatId],
                                summary: null,
                                messages: [],
                                lastMessageAt: new Date(),
                            }) as unknown as ChatRecord,
                    )
                // The pages loaded beyond the first stay, for the same user
                // and agent: a reload after a turn would otherwise take the
                // list back to the newest page
                const owner = `${id}:${userId.value ?? ''}`
                if (owner !== conversationsOwner) {
                    conversationsPage.value = 1
                }
                const leftRunningIds = new Set(leftRunning.map((c) => c.id))
                const older =
                    conversationsPage.value > 1
                        ? conversations.value.filter(
                              (c) =>
                                  !listedIds.has(c.id) &&
                                  !leftRunningIds.has(c.id),
                          )
                        : []
                conversations.value = [...leftRunning, ...listed, ...older]
                conversationsOwner = owner
                if (conversationsPage.value === 1) {
                    hasMoreConversations.value =
                        listed.length >= CONVERSATIONS_PAGE_SIZE
                }
            } catch {
                conversations.value = []
                conversationsPage.value = 1
                conversationsOwner = undefined
                hasMoreConversations.value = false
                return
            } finally {
                isLoadingConversations.value = false
            }
            // The turns left running first: the server reads at most so many.
            void refreshRunningTurns([
                ...new Set([
                    ...watchedTurns(),
                    ...conversations.value.map(
                        (conversation) => conversation.id,
                    ),
                ]),
            ])
        }

        /** The next page of the list, appended (the end of the sidebar) */
        async function loadMoreConversations() {
            if (
                !hasMoreConversations.value ||
                isLoadingMoreConversations.value ||
                isReasoningAgent.value
            ) {
                return
            }
            isLoadingMoreConversations.value = true
            const owner = conversationsOwner
            try {
                const page = conversationsPage.value + 1
                const listed = await apiClient.value.getConversations(
                    agentIdRef.value,
                    userId.value,
                    { page },
                )
                // Another user's list arrived meanwhile: this page is not its
                if (owner !== conversationsOwner) {
                    return
                }
                // A chat started meanwhile moved every row one down: the
                // first of this page can be the last of the previous one
                const known = new Set(conversations.value.map((c) => c.id))
                conversations.value = [
                    ...conversations.value,
                    ...listed.filter((c) => !known.has(c.id)),
                ]
                conversationsPage.value = page
                hasMoreConversations.value =
                    listed.length >= CONVERSATIONS_PAGE_SIZE
            } catch {
                // The next scroll to the end asks again
            } finally {
                isLoadingMoreConversations.value = false
            }
        }

        /**
         * The user's chats whose title matches, from the server, so a chat
         * beyond the loaded pages is found too. Not kept in the list.
         */
        async function searchConversations(
            search: string,
        ): Promise<ChatRecord[]> {
            if (isReasoningAgent.value) {
                return []
            }
            return apiClient.value.getConversations(
                agentIdRef.value,
                userId.value,
                { search },
            )
        }

        // Files uploaded in the current chat (the user's "My files" entries
        // tied to this conversation). No chat → nothing to load.
        async function loadChatFiles() {
            const chatId = localChatId.value
            if (!chatId) {
                chatFiles.value = []
                return
            }
            isLoadingChatFiles.value = true
            try {
                chatFiles.value = await apiClient.value.getChatFiles(chatId)
            } catch {
                chatFiles.value = []
            } finally {
                isLoadingChatFiles.value = false
            }
        }

        function getChatFileUrl(documentId: string, inline = false) {
            return apiClient.value.getChatFileDownloadUrl(documentId, inline)
        }

        function getChatFilePreviewUrl(documentId: string) {
            return apiClient.value.getChatFilePreviewUrl(documentId)
        }

        // Exclude a file from the user's KB (soft-delete server-side); drop it
        // from the local list on success. A spreadsheet is deleted with every
        // version written of it, so they all leave the list.
        async function deleteChatFile(documentId: string) {
            await apiClient.value.deleteChatFile(documentId)
            const removed = chatFiles.value.find(
                (file) => file.documentId === documentId,
            )
            const rootId =
                removed?.kind === 'spreadsheet'
                    ? (removed.sourceAttachmentId ?? removed.documentId)
                    : undefined
            chatFiles.value = chatFiles.value.filter(
                (file) =>
                    file.documentId !== documentId &&
                    (!rootId ||
                        (file.documentId !== rootId &&
                            file.sourceAttachmentId !== rootId)),
            )
        }

        async function loadChat(id: string, chatId: string) {
            // Reasoning chats are never persisted and the chat is
            // recreated empty on every chatId/agentId change: nothing to load.
            if (isReasoningAgent.value) {
                return
            }
            try {
                const data = await apiClient.value.getChat(id, chatId)
                // The chat may have advanced while the request was in flight
                // (the user submitted a message, or the chatId rotated): never
                // clobber local messages with a stale response. A wiped list
                // mid-stream also suppresses the synthetic welcome message,
                // because the streamed reply gets re-pushed at index 0.
                if (
                    chatId !== localChatId.value ||
                    chat.value.status !== 'ready' ||
                    chat.value.messages.length > 0
                ) {
                    return
                }
                if (data) {
                    chat.value.messages = data.messages as UIChatMessage[]
                    isConversationBlocked.value = data.blocked ?? false
                    void continueAfterLoad(
                        id,
                        chatId,
                        data.turnInProgress === true,
                    )
                }
            } catch {
                // A new chat 404s here: the instance already starts empty,
                // clearing messages would race with the first submit.
            }
        }

        /**
         * What a chat just opened still has running: its turn, followed to
         * the end, then the page reads its answer left open, which run on
         * the stored answer rather than on the replay, and last what the user
         * queued in the chat.
         */
        async function continueAfterLoad(
            id: string,
            chatId: string,
            turnInProgress: boolean,
        ) {
            if (turnInProgress) {
                await followTurn(chatId)
                // Left meanwhile, or already sent on: the messages on screen
                // are no longer this chat's answer.
                if (chatId !== localChatId.value || isTurnInFlight()) {
                    return
                }
            }
            await resumeAfterPageNavigation(id, chatId)
            if (chatId === localChatId.value) {
                await flushQueue()
            }
        }

        /**
         * Follows the turn still running in a chat just opened (spec
         * resumable-chat-streams): its answer streams in from the start, the
         * status is `streaming` meanwhile (so nothing else is sent), and when
         * the stream ends the chat is read again until the turn is written.
         * The stream also ends with nothing in it: the turn finished between
         * the read and the resume, or the replica running it is gone, which
         * the server stops reporting within a minute and a half.
         */
        async function followTurn(chatId: string) {
            const turn = { chatId }
            followedTurn = turn
            followedChatId.value = chatId
            const isFollowing = () =>
                followedTurn === turn && chatId === localChatId.value
            const deadline = Date.now() + FOLLOW_TURN_TIMEOUT_MS
            try {
                while (
                    isFollowing() &&
                    !isTurnInFlight() &&
                    Date.now() < deadline
                ) {
                    isResumingTurn = true
                    try {
                        await chat.value.resumeStream()
                    } finally {
                        isResumingTurn = false
                    }
                    if (!isFollowing()) {
                        return
                    }
                    chat.value.clearError()
                    const data = await apiClient.value
                        .getChat(agentIdRef.value, chatId)
                        .catch(() => null)
                    if (!data || !isFollowing() || isTurnInFlight()) {
                        return
                    }
                    if (!data.turnInProgress) {
                        chat.value.messages = data.messages as UIChatMessage[]
                        return
                    }
                    await new Promise((resolve) =>
                        setTimeout(resolve, FOLLOW_TURN_RETRY_MS),
                    )
                }
            } finally {
                if (followedTurn === turn) {
                    followedTurn = undefined
                    followedChatId.value = undefined
                }
            }
        }

        /**
         * After a navigation the agent started (spec `page-tools.md`), the
         * answer that clicked goes on on the backend and usually asks to read
         * the new page, but its stream stayed with the old one: that call
         * waits here. Runs the pending READ-ONLY page calls of the last answer
         * on this page, never a write, and only within a minute of the click.
         * The backend may still be finishing that answer, so the stored chat
         * is read again for a while.
         */
        async function resumeAfterPageNavigation(id: string, chatId: string) {
            let clickedAt: number
            try {
                clickedAt = Number(sessionStorage.getItem(PAGE_NAVIGATION_KEY))
                sessionStorage.removeItem(PAGE_NAVIGATION_KEY)
            } catch {
                return
            }
            // Switched off by the user: the answer waits for the next message.
            if (
                !clickedAt ||
                Date.now() - clickedAt > 60_000 ||
                pageAccessOff.value
            ) {
                return
            }
            for (let attempt = 0; attempt < 12; attempt++) {
                const last = chat.value.messages[chat.value.messages.length - 1]
                if (last?.role === 'assistant') {
                    const open = last.parts.filter(
                        (part) =>
                            isHostToolPart(part) &&
                            (part as { state?: string }).state ===
                                'input-available',
                    ) as unknown as Array<{
                        toolCallId: string
                        toolName: string
                        input: unknown
                        toolMetadata?: { provider?: string; readOnly?: boolean }
                    }>
                    const reads = open.filter(
                        (part) =>
                            part.toolMetadata?.provider === 'page' &&
                            part.toolMetadata.readOnly === true,
                    )
                    if (open.length > 0) {
                        // A write left open is never replayed: the next
                        // message closes it as an unknown outcome. Checked
                        // again here: the user may have switched page access
                        // off while the history was being read.
                        if (
                            reads.length === open.length &&
                            !pageAccessOff.value
                        ) {
                            for (const part of reads) {
                                hostToolRunner.enqueue({
                                    toolCallId: part.toolCallId,
                                    toolName: part.toolName,
                                    input: part.input,
                                    messageId: last.id,
                                    readOnly: true,
                                    provider: 'page',
                                })
                            }
                        }
                        return
                    }
                    if (last.parts[last.parts.length - 1]?.type === 'text') {
                        return
                    }
                }
                await new Promise((resolve) => setTimeout(resolve, 1500))
                if (
                    chatId !== localChatId.value ||
                    chat.value.status !== 'ready'
                ) {
                    return
                }
                try {
                    const data = await apiClient.value.getChat(id, chatId)
                    if (data && chatId === localChatId.value) {
                        chat.value.messages = data.messages as UIChatMessage[]
                    }
                } catch {
                    return
                }
            }
        }

        /**
         * A written turn is in flight, so the message list must not be replaced
         * under it. Read through a widened local on purpose: the status changes
         * across an await, which the compiler otherwise narrows away.
         */
        const isTurnInFlight = (): boolean => {
            const status: string = chat.value.status
            return status === 'submitted' || status === 'streaming'
        }

        /**
         * The answer on screen is not over: its request runs, it is followed
         * from another page, or the embedding page runs its calls (the chat
         * reads `ready` then, and the input shows the work and its stop).
         */
        const isTurnBusy = (): boolean =>
            isTurnInFlight() ||
            followedTurn?.chatId === localChatId.value ||
            hostToolsPending.value > 0

        /**
         * The answer waits on the user: an approval, the consent to a host
         * call, a form or another tool only the user answers.
         */
        const awaitsUser = (): boolean => {
            const last = lastChatMessage()
            return (
                hostConfirmation.value !== undefined ||
                awaitsApproval(last) ||
                awaitsToolAnswer(last)
            )
        }

        /** The chats whose queue is waiting for its turn to be written. */
        const flushingChats = new Set<string>()
        /**
         * Sends what the user queued in the chat on screen once its turn is
         * over and written: the server builds the history from the stored
         * chat and the request carries only the new message, so a message
         * sent before the write would reach the agent without the answer it
         * follows. The write follows the end of the stream: waited for a few
         * seconds at most. An answer that waits on the user (a chat opened
         * again on its form) gets the queue back in the input instead.
         */
        async function flushQueue() {
            const chatId = localChatId.value
            if (
                flushingChats.has(chatId) ||
                queuedMessages.value.length === 0 ||
                isTurnBusy() ||
                chatError.value
            ) {
                return
            }
            if (awaitsUser()) {
                restoreInput(takeQueue(chatId))
                return
            }
            flushingChats.add(chatId)
            try {
                const deadline = Date.now() + QUEUE_WRITE_WAIT_MS
                while (Date.now() < deadline) {
                    const running = await Promise.race([
                        apiClient.value
                            .getRunningTurns(agentIdRef.value, [chatId])
                            .catch((): string[] => []),
                        new Promise<string[]>((resolve) =>
                            setTimeout(
                                () => resolve([chatId]),
                                QUEUE_WRITE_ASK_MS,
                            ),
                        ),
                    ])
                    if (!running.includes(chatId)) {
                        break
                    }
                    await new Promise((resolve) =>
                        setTimeout(resolve, QUEUE_WRITE_RETRY_MS),
                    )
                }
                // Left meanwhile, or the turn went on by itself (a host
                // result, an answered tool): the queue waits for its end.
                if (chatId !== localChatId.value || isTurnBusy()) {
                    return
                }
                const text = takeQueue(chatId)
                if (text) {
                    submitTurn(text)
                }
            } finally {
                flushingChats.delete(chatId)
            }
        }

        /** Ordering of the concurrent reloads: see `refreshMessages`. */
        let refreshSequence = 0
        let refreshApplied = 0

        /**
         * Replaces the message list with what the server persisted.
         *
         * `loadChat` deliberately refuses to touch a non-empty list, because it
         * runs on mount and must never clobber a stream in flight. A voice turn
         * is the opposite case: the answer was produced by the provider calling
         * our backend, so the persisted chat is ahead of what this client shows
         * (the transcript we appended is text only, without sources or tool
         * results). Still guarded on an idle chat, for the same reason as there.
         *
         * Returns 'pending' when the server was still BEHIND what the client
         * shows, so the caller comes back for the rest: a USER line with no
         * persisted counterpart means the turn it belongs to has not been
         * written yet, and with it are missing the tool results and sources
         * that only the reload can bring (an agent line proves nothing: the
         * spoken fallback sentence exists only in the audio stream). Returns
         * 'caught-up' only when a reload was actually applied and nothing is
         * missing; everything else (a failed request, a stale response, a
         * written turn in flight) is 'indeterminate', which a poller must
         * treat as "try again", not as done: a killed voice turn persists
         * minutes after the call, and stopping on a failure left it off the
         * screen until the page was reloaded by hand (seen live 2026-08-26).
         *
         * A later reload wins over an earlier one, whatever order they answer
         * in: out-of-order answers are the normal case on a call, and a slow
         * fetch that started first would otherwise land last and take the
         * newest messages, their tool results and their sources off the screen.
         */
        async function refreshMessages(): Promise<
            'pending' | 'caught-up' | 'indeterminate'
        > {
            // Only a turn in flight is a reason not to reload. Requiring
            // 'ready' also refused to reload after a failed submit ('error'),
            // which on a call left the conversation frozen on screen until the
            // page was reloaded by hand.
            if (isReasoningAgent.value || isTurnInFlight()) {
                return 'indeterminate'
            }
            const chatId = localChatId.value
            const mine = (refreshSequence += 1)
            try {
                const data = await apiClient.value.getChat(
                    agentIdRef.value,
                    chatId,
                )
                if (
                    !data ||
                    chatId !== localChatId.value ||
                    isTurnInFlight() ||
                    mine <= refreshApplied
                ) {
                    return 'indeterminate'
                }
                refreshApplied = mine
                const persisted = data.messages as UIChatMessage[]
                const pending = unpersistedVoiceLines(
                    persisted,
                    chat.value.messages,
                )
                chat.value.messages = [...persisted, ...pending]
                isConversationBlocked.value = data.blocked ?? false
                return pending.length > 0 ? 'pending' : 'caught-up'
            } catch {
                // Nothing persisted yet (a new conversation is created by its
                // first write, so until then this request has nothing to
                // find), or a fetch that failed: either way not proof of
                // anything, so not 'caught-up'.
            }
            return 'indeterminate'
        }

        /**
         * Appends a message the client already knows about, without sending
         * anything: the voice transcript, whose turn is answered server-side.
         */
        function appendMessage(message: UIChatMessage) {
            chat.value.messages = [...chat.value.messages, message]
        }

        async function loadMessageFeedbacks(chatId: string) {
            try {
                messageFeedbacks.value =
                    await apiClient.value.getMessageFeedbacks(chatId)
            } catch {
                messageFeedbacks.value = []
            }
        }

        function upsertLocalFeedback(feedback: MessageFeedback) {
            const idx = messageFeedbacks.value.findIndex(
                (f) => f.messageId === feedback.messageId,
            )
            if (idx >= 0) {
                messageFeedbacks.value[idx] = feedback
            } else {
                messageFeedbacks.value.push(feedback)
            }
        }

        function extractFeedbackContext(message: UIChatMessage) {
            const msgList = messages.value
            const idx = msgList.findIndex((m) => m.id === message.id)
            const assistantMessage = getTextPart(message) ?? ''
            const prevUser = msgList
                .slice(0, idx)
                .reverse()
                .find((m) => m.role === 'user')
            const userQuestion = getTextPart(prevUser ?? message) ?? ''
            return { userQuestion, assistantMessage }
        }

        // === Actions ===
        async function initialize(id: string, chatId: string) {
            await Promise.all([
                loadChat(id, chatId),
                loadConversations(id),
                loadMessageFeedbacks(chatId),
            ])

            if (!storedGeolocation.value) {
                collectGeolocation((lat, lon) =>
                    apiClient.value.reverseGeocode(lat, lon, locale.value),
                ).then((geo) => {
                    if (geo) {
                        storedGeolocation.value = geo
                    }
                })
            }
        }

        async function handleSubmit() {
            // Snapshot text and attachments before any async operation to avoid
            // them being cleared by startNewChat() (reasoning agents) or by a
            // concurrent store update from the component.
            const text = input.value.trim()
            const fileParts: FileUIPart[] = pendingAttachments.value
                .filter(
                    (
                        att,
                    ): att is PendingAttachment & {
                        state: 'done'
                        mediaSource: NonNullable<
                            PendingAttachment['mediaSource']
                        >
                    } => att.state === 'done' && att.mediaSource !== undefined,
                )
                .map((att) => {
                    return {
                        type: 'file' as const,
                        mediaType: att.mediaSource.mimeType,
                        url: att.localUrl,
                        filename: att.mediaSource.name,
                        providerMetadata: {
                            '8bot': {
                                media: att.mediaSource,
                            },
                        },
                    }
                })

            const pointer = pagePointerAvailable.value
                ? pagePointer.value
                : undefined

            // While a turn runs the message waits for it, and goes with the
            // others queued when the turn ends. Text only: attaching and
            // pointing stay off during a turn.
            if (!isReasoningAgent.value && isTurnBusy()) {
                if (text && fileParts.length === 0 && !pointer) {
                    setQueue(localChatId.value, [...queuedMessages.value, text])
                    input.value = ''
                }
                return
            }

            if (isReasoningAgent.value && messages.value.length > 0) {
                await startNewChat()
            }

            submitTurn(text, fileParts, pointer)
            input.value = ''
            pagePointer.value = undefined
            clearPendingAttachments()
        }

        /** Sends the user's message, ready to be rolled back by a Stop. */
        function submitTurn(
            text: string,
            fileParts: FileUIPart[] = [],
            pointer?: PagePointer,
        ) {
            // A host call still pending belongs to the turn the user is
            // leaving: its result must not land on the next one.
            cancelHostCalls()

            // Snapshot state before sending so we can rollback on stop
            lastSubmittedInput.value = text
            lastSubmittedPointer.value = pointer
            messageCountBeforeSubmit.value = chat.value.messages.length

            // The chat's sendMessage can't resolve the correct overload
            // when using a custom message generic: the cast is intentional.
            const sendMessage = chat.value.sendMessage as unknown as (msg: {
                text: string
                files?: FileUIPart[]
                metadata?: UIChatMessageMetadata
            }) => void
            sendMessage({
                text,
                files: fileParts.length > 0 ? fileParts : undefined,
                ...(pointer && { metadata: { pagePointer: pointer } }),
            })
        }

        /**
         * Programmatically send a user message without the input snapshot/rollback
         * logic. Logs a warning and does nothing if the chat is not ready.
         */
        function sendMessage(text: string) {
            const trimmed = text.trim()
            if (!trimmed) {
                return
            }
            if (chat.value.status !== 'ready') {
                logger.warn(
                    '[useChatbotStore] sendMessage called before chat is ready. Listen for the "ready" event on the widget element.',
                )
                return
            }
            const send = chat.value.sendMessage as unknown as (msg: {
                text: string
            }) => void
            cancelHostCalls()
            send({ text: trimmed })
        }

        /**
         * The user's Stop. The turn is stopped server-side too, where it would
         * otherwise go on and be written (spec resumable-chat-streams), so the
         * rollback below matches what a reload shows. A turn that had already
         * ended is stored whole: the chat is read again instead.
         */
        async function stopGeneration() {
            // An answer that already called the host stays: the app or the
            // page may have acted on it, and the rollback would hide what
            // they did (spec `page-tools.md`, Ferma).
            const answerCalledHost = chat.value.messages
                .slice(messageCountBeforeSubmit.value)
                .some((message) => message.parts.some(isHostToolPart))
            // Read before the stop: the stop turns the chat `ready`, and the
            // watcher that clears the rollback state on `ready` can run before
            // the stop resolves, which put the messages back with an empty
            // input.
            const submittedText = lastSubmittedInput.value
            const submittedPointer = lastSubmittedPointer.value
            const shouldRollback = submittedText.length > 0 && !answerCalledHost
            const chatId = localChatId.value
            const countBeforeSubmit = messageCountBeforeSubmit.value
            // A turn followed from another page has nothing to roll back
            // here: once stopped, the chat as stored has no trace of it.
            const wasFollowing = followedTurn?.chatId === chatId
            // This page's own turn, which a turn sent from another page may
            // have replaced as the chat's running one.
            const streamId = turnStreamId
            // What the user queued is not sent after a Stop: it goes back to
            // the input, after the question a rollback puts there and before
            // what the user is still writing. Taken before the stop, which
            // turns the chat `ready` and would send it.
            const queuedText = takeQueue(chatId)
            const restoreInputAfterStop = (question = '') => {
                input.value = [question, queuedText, input.value]
                    .filter(Boolean)
                    .join(QUEUE_SEPARATOR)
            }
            // Moved on while the server was asked (another chat on screen, or
            // the next message already sent): what is on screen now is not
            // this turn's to roll back, and the queue waits in its chat.
            const movedOn = () => {
                if (
                    chatId === localChatId.value &&
                    !isTurnInFlight() &&
                    messageCountBeforeSubmit.value === countBeforeSubmit
                ) {
                    return false
                }
                if (queuedText) {
                    setQueue(chatId, [
                        queuedText,
                        ...(queues.value[chatId] ?? []),
                    ])
                }
                return true
            }
            followedTurn = undefined
            followedChatId.value = undefined
            // Before the stop: a host result landing after it must not resume
            // the turn the user just stopped.
            cancelHostCalls()
            await chat.value.stop()
            // Not stopped server-side: a reasoning chat, never stored, and an
            // answer that called the host from this page, which stays and
            // must stay on a reload too (a followed turn runs no host call).
            // A Stop the server did not get is rolled back as before.
            const stopped =
                isReasoningAgent.value ||
                (answerCalledHost && !wasFollowing) ||
                (await apiClient.value
                    .stopChatTurn(agentIdRef.value, chatId, streamId)
                    .catch(() => true))
            if (movedOn()) {
                return
            }
            if (wasFollowing || !stopped) {
                await refreshMessages()
                // The same, while the chat was read again.
                if (movedOn()) {
                    return
                }
                if (
                    wasFollowing ||
                    chat.value.messages.length > messageCountBeforeSubmit.value
                ) {
                    lastSubmittedInput.value = ''
                    restoreInputAfterStop()
                    return
                }
                // Nothing stored either: the turn never reached the server.
            }
            if (shouldRollback) {
                restoreInputAfterStop(submittedText)
                // The chip comes back with its question, unless the user
                // pointed at something else meanwhile
                pagePointer.value ??= submittedPointer
                chat.value.messages = chat.value.messages.slice(
                    0,
                    messageCountBeforeSubmit.value,
                )
                lastSubmittedInput.value = ''
                return
            }
            restoreInputAfterStop()
        }

        // A history replaced under a waiting question (a reload from the
        // server that no longer carries the call): nobody can answer it, and
        // the queue would wait on it for ever. A call's page action is never
        // in the history under the widget's id: its question waits for the
        // user, whatever the reloads of the call bring.
        watch(
            () => chat.value.messages,
            (current) => {
                const waiting = hostConfirmation.value?.toolCallId
                if (
                    waiting &&
                    !voicePageCalls.has(waiting) &&
                    !current.some((message) =>
                        message.parts.some(
                            (part) =>
                                (part as { toolCallId?: string }).toolCallId ===
                                waiting,
                        ),
                    )
                ) {
                    cancelHostCalls()
                }
            },
        )

        // A request starting: what a deferred page click waits for. Its turn
        // names itself in its stream: until then a Stop names no turn, never
        // the one before it.
        watch(
            () => chat.value.status,
            (status) => {
                if (status === 'submitted') {
                    requestCount.value++
                    turnStreamId = undefined
                }
            },
        )

        // The page control bar stays up for the whole response: between two
        // actions the chat is ready for a moment, before the resume starts.
        // A call's actions keep it up too: the chat does not move during them.
        let pageControlTimer: ReturnType<typeof setTimeout> | undefined
        watch(
            () =>
                [
                    chat.value.status,
                    hostToolsPending.value,
                    voicePageCallsPending.value,
                ] as const,
            ([status, pending, voicePending]) => {
                clearTimeout(pageControlTimer)
                if (status === 'ready' && pending === 0 && voicePending === 0) {
                    pageControlTimer = setTimeout(() => {
                        pageControlActive.value = false
                    }, 1500)
                }
            },
        )

        // === Working state (spec `widget-copilot.md`, D4) ===
        // On at once, off only after a quiet moment: between the result of a
        // call and the resume it starts the chat is ready for an instant, and
        // the signal would blink at every step. A confirmation card turns it
        // off, because the turn waits on the user then: the card of a host
        // call, or the approval of a tool the backend runs.
        const isWorking = ref(false)
        let workingTimer: ReturnType<typeof setTimeout> | undefined
        watch(
            () =>
                !hostConfirmation.value &&
                !awaitsApproval(lastChatMessage()) &&
                (isTurnInFlight() || hostToolsPending.value > 0),
            (busy) => {
                clearTimeout(workingTimer)
                if (busy) {
                    isWorking.value = true
                    return
                }
                workingTimer = setTimeout(() => {
                    isWorking.value = false
                }, WORKING_SETTLE_MS)
            },
            { immediate: true },
        )

        // === Turns running in the background (spec resumable-chat-streams, F2) ===
        /**
         * The chats the list marks as answering: the chat on screen from its
         * own state (working here, or followed), the others as the server
         * last said.
         */
        const runningChatIds = computed(() => {
            const others = serverRunningChatIds.value.filter(
                (id) => id !== localChatId.value,
            )
            return isWorking.value || followedChatId.value === localChatId.value
                ? [...others, localChatId.value]
                : others
        })

        /** The turns worth asking about: running elsewhere, or left running. */
        const watchedTurns = () => [
            ...new Set([
                ...serverRunningChatIds.value,
                ...backgroundTurns.value,
            ]),
        ]

        const documentVisibility = useDocumentVisibility()
        let runningTurnsTimer: ReturnType<typeof setTimeout> | undefined
        /** Ordering of the concurrent reads, as for `refreshMessages`. */
        let runningTurnsSequence = 0
        let runningTurnsApplied = 0

        /** Asks again in a while, while there is a turn to watch and anyone to see it. */
        function scheduleRunningTurns() {
            clearTimeout(runningTurnsTimer)
            runningTurnsTimer = undefined
            if (
                watchedTurns().length === 0 ||
                documentVisibility.value !== 'visible'
            ) {
                return
            }
            runningTurnsTimer = setTimeout(() => {
                runningTurnsTimer = undefined
                void refreshRunningTurns(watchedTurns())
            }, RUNNING_TURNS_POLL_MS)
        }

        /**
         * Reads which of the chats have a turn running and settles the ones
         * that ended: the list loses their mark, a turn left in the background
         * leaves a notice (unless its chat is on screen again), and the list
         * is read again once, for the titles and the order the written turns
         * changed.
         */
        async function refreshRunningTurns(chatIds: string[]) {
            // Beyond the server's limit an id is not asked, so never taken as
            // over: the callers put the turns left running first.
            const askedIds = chatIds.slice(0, RUNNING_TURNS_MAX_IDS)
            if (isReasoningAgent.value || askedIds.length === 0) {
                scheduleRunningTurns()
                return
            }
            const mine = (runningTurnsSequence += 1)
            let running: string[]
            try {
                running = await apiClient.value.getRunningTurns(
                    agentIdRef.value,
                    askedIds,
                )
            } catch {
                scheduleRunningTurns()
                return
            }
            if (mine <= runningTurnsApplied) {
                return
            }
            runningTurnsApplied = mine
            for (const id of running) {
                seenRunning.add(id)
            }
            const asked = new Set(askedIds)
            const absent = watchedTurns().filter(
                (id) => asked.has(id) && !running.includes(id),
            )
            // A turn left before its request reached the server is not over
            // while it has never been reported: it stays watched for a
            // while, then is dropped without a notice (it never started).
            const unstarted = (id: string) =>
                backgroundTurns.value.includes(id) && !seenRunning.has(id)
            const expired = absent.filter(
                (id) =>
                    unstarted(id) &&
                    Date.now() - (backgroundLeftAt.get(id) ?? 0) >
                        BACKGROUND_START_GRACE_MS,
            )
            const ended = absent.filter((id) => !unstarted(id))
            serverRunningChatIds.value = [
                ...serverRunningChatIds.value.filter((id) => !asked.has(id)),
                ...running,
            ]
            const endedInBackground = ended.filter((id) =>
                backgroundTurns.value.includes(id),
            )
            if (endedInBackground.length > 0 || expired.length > 0) {
                backgroundTurns.value = backgroundTurns.value.filter(
                    (id) =>
                        !endedInBackground.includes(id) &&
                        !expired.includes(id),
                )
                answeredInBackground.value = [
                    ...answeredInBackground.value,
                    ...endedInBackground.filter(
                        (id) =>
                            id !== localChatId.value &&
                            !answeredInBackground.value.includes(id),
                    ),
                ]
            }
            if (ended.length > 0) {
                void loadConversations(agentIdRef.value)
            }
            scheduleRunningTurns()
        }

        watch(documentVisibility, (visibility) => {
            if (visibility === 'visible') {
                void refreshRunningTurns(watchedTurns())
                return
            }
            clearTimeout(runningTurnsTimer)
            runningTurnsTimer = undefined
        })
        onScopeDispose(() => clearTimeout(runningTurnsTimer))

        /** The answers that ended in the background, named by the chat's title, else its question. */
        const backgroundAnswers = computed(() =>
            answeredInBackground.value.map((chatId) => ({
                chatId,
                title:
                    conversations.value.find((c) => c.id === chatId)?.title ||
                    backgroundQuestions.value[chatId] ||
                    undefined,
            })),
        )
        /** The notice's "Open": the chat whose answer ended in the background. */
        function openBackgroundAnswer(chatId: string) {
            forgetBackgroundTurn(chatId)
            localChatId.value = chatId
            navigate('chat')
        }
        function dismissBackgroundAnswer(chatId: string) {
            answeredInBackground.value = answeredInBackground.value.filter(
                (id) => id !== chatId,
            )
            forgetBackgroundQuestion(chatId)
        }

        // The turn on screen ended: what the user queued goes now, unless the
        // turn failed or waits on the user, when it goes back to the input.
        // A status that changes with the chat (another conversation opened)
        // is not an end: the chat opened sends its queue once loaded.
        watch(
            () => [chat.value.status, chat.value.id] as const,
            ([status, id], [previous, previousId]) => {
                if (id !== previousId) {
                    return
                }
                // A resume that fails is not the end of the turn it follows.
                if (status === 'error') {
                    if (followedTurn?.chatId !== localChatId.value) {
                        restoreInput(takeQueue(localChatId.value))
                    }
                    return
                }
                if (
                    status === 'ready' &&
                    (previous === 'streaming' || previous === 'submitted')
                ) {
                    if (awaitsUser()) {
                        restoreInput(takeQueue(localChatId.value))
                        return
                    }
                    void flushQueue()
                }
            },
        )
        // The host calls of the answer done, with nothing that resumes it:
        // the turn is over.
        watch(hostToolsPending, (pending) => {
            if (pending === 0 && chat.value.status === 'ready') {
                void flushQueue()
            }
        })

        // Clear rollback state when generation completes naturally
        watch(
            () => chat.value.status,
            (status) => {
                if (status === 'ready' && lastSubmittedInput.value.length > 0) {
                    lastSubmittedInput.value = ''
                }
            },
        )

        function clearPendingAttachments(revokeAll = false) {
            for (const att of pendingAttachments.value) {
                // 'done' attachment localUrls may still be referenced in
                // FileUIPart.url inside message parts — skip revocation unless
                // explicitly requested (e.g. when starting a new chat).
                if (revokeAll || att.state !== 'done') {
                    URL.revokeObjectURL(att.localUrl)
                }
            }
            pendingAttachments.value = []
            fileLimitError.value = null
        }

        async function startNewChat() {
            lastSubmittedInput.value = ''
            hostStepTitles.value = {}
            // Not a Stop: the turn of the chat left behind goes on and is
            // written, the browser only detaches from it (see the watcher on
            // the chatId), and its host calls are cancelled by theirs.
            localChatId.value = generateId()
            // `useChat` recreates the chat for the new id on the next tick:
            // wait for it, so this function and its callers (a reasoning
            // agent's submit, the identity switch) act on the new chat.
            await nextTick()
            chat.value.clearError()
            chatError.value = undefined
            isConversationBlocked.value = false
            clearPendingAttachments(true)
            navigate('chat')
            return localChatId.value
        }

        async function regenerate(
            options?: Parameters<typeof chat.value.regenerate>[0],
        ) {
            if (chat.value.status !== 'ready') {
                lastSubmittedInput.value = ''
                await stopGeneration()
            }
            cancelHostCalls()
            await chat.value.regenerate(options)
        }

        function addToolOutput(params: {
            tool: string
            toolCallId: string
            output: unknown
        }) {
            if (params.tool === 'requestGeolocation') {
                const output = params.output as
                    | {
                          latitude: number
                          longitude: number
                          accuracy?: number
                          city?: string
                      }
                    | { error: string }
                if ('latitude' in output) {
                    storedGeolocation.value = {
                        geolocationLatitude: output.latitude,
                        geolocationLongitude: output.longitude,
                        geolocationAccuracy: output.accuracy,
                        geolocationCity: output.city,
                    }
                }
            }
            chat.value.addToolOutput(params)
        }

        /**
         * Answers a tool approval request. The response rides the same chat
         * stream: `sendAutomaticallyWhen` submits it, the backend re-validates
         * it against the persisted request and the SDK resumes (or denies) the
         * suspended call.
         */
        function addToolApprovalResponse(params: {
            id: string
            approved: boolean
            reason?: string
        }) {
            chat.value.addToolApprovalResponse(params)
        }

        // Files already committed to prior messages in this chat.
        const sentFileCount = computed(() =>
            chat.value.messages.reduce(
                (n, m) => n + m.parts.filter((p) => p.type === 'file').length,
                0,
            ),
        )

        // Single source of truth for the attachment limits (see AgentFileUpload).
        // The per-message cap counts the current draft's attachments; the
        // per-chat cap counts those plus everything already sent.
        const maxFilesPerMessage = computed(
            () => agentFileUpload.value?.maxFilesPerMessage ?? 5,
        )
        const maxFilesPerChat = computed(
            () => agentFileUpload.value?.maxFilesPerChat ?? 20,
        )
        const remainingAttachmentSlots = computed(() =>
            Math.max(
                0,
                Math.min(
                    maxFilesPerMessage.value - pendingAttachments.value.length,
                    maxFilesPerChat.value -
                        sentFileCount.value -
                        pendingAttachments.value.length,
                ),
            ),
        )
        const isAttachmentLimitReached = computed(
            () => remainingAttachmentSlots.value <= 0,
        )

        // Reactive notice surfaced to the input when a file gets rejected.
        const fileLimitError = ref<{
            reason: 'per-message' | 'per-chat'
            max: number
        } | null>(null)
        const clearFileLimitError = () => {
            fileLimitError.value = null
        }

        // The notice is only true while at capacity: drop it once room frees up.
        watch(isAttachmentLimitReached, (reached) => {
            if (!reached) {
                fileLimitError.value = null
            }
        })

        async function handleFileSelect(file: File) {
            if (isAttachmentLimitReached.value) {
                // Whichever cap is tighter is the one that blocked this file.
                fileLimitError.value =
                    maxFilesPerChat.value -
                        sentFileCount.value -
                        pendingAttachments.value.length <=
                    maxFilesPerMessage.value - pendingAttachments.value.length
                        ? { reason: 'per-chat', max: maxFilesPerChat.value }
                        : {
                              reason: 'per-message',
                              max: maxFilesPerMessage.value,
                          }
                return
            }
            const id = generateId()
            const localUrl = URL.createObjectURL(file)
            const attachment: PendingAttachment = {
                id,
                file,
                localUrl,
                state: 'uploading',
            }
            pendingAttachments.value = [...pendingAttachments.value, attachment]

            try {
                const mediaSource = await apiClient.value.uploadMedia(
                    agentIdRef.value,
                    localChatId.value,
                    file,
                )
                pendingAttachments.value = pendingAttachments.value.map((a) =>
                    a.id === id ? { ...a, state: 'done', mediaSource } : a,
                )
            } catch (err) {
                const message =
                    err instanceof Error ? err.message : 'Upload failed'
                pendingAttachments.value = pendingAttachments.value.map((a) =>
                    a.id === id ? { ...a, state: 'error', error: message } : a,
                )
            }
        }

        async function submitVote(
            message: UIChatMessage,
            vote: 'upvote' | 'downvote',
        ) {
            const { userQuestion, assistantMessage } =
                extractFeedbackContext(message)
            const feedback = await apiClient.value.submitMessageFeedback({
                messageId: message.id,
                chatId: localChatId.value,
                agentId: agentIdRef.value,
                vote,
                userQuestion,
                assistantMessage,
            })
            upsertLocalFeedback(feedback)
        }

        async function onUpvote(message: UIChatMessage) {
            await submitVote(message, 'upvote')
        }

        async function onDownvote(message: UIChatMessage) {
            await submitVote(message, 'downvote')
        }

        function onFeedback(message: UIChatMessage) {
            isFeedbackSubmitted.value = false
            feedbackSubmitError.value = undefined
            feedbackDialogMessage.value = message
        }

        async function onFeedbackSubmit(comment: string) {
            if (!feedbackDialogMessage.value) {
                return
            }
            try {
                isFeedbackSubmitting.value = true
                isFeedbackSubmitted.value = false
                feedbackSubmitError.value = undefined
                const { userQuestion, assistantMessage } =
                    extractFeedbackContext(feedbackDialogMessage.value)
                const feedback = await apiClient.value.submitMessageFeedback({
                    messageId: feedbackDialogMessage.value.id,
                    chatId: localChatId.value,
                    agentId: agentIdRef.value,
                    userQuestion,
                    assistantMessage,
                    comment,
                })
                upsertLocalFeedback(feedback)
                isFeedbackSubmitted.value = true
                await loadMessageFeedbacks(localChatId.value)
            } catch {
                feedbackSubmitError.value = te('message.feedbackSubmitError')
                    ? t('message.feedbackSubmitError')
                    : 'Failed to submit feedback'
            } finally {
                isFeedbackSubmitting.value = false
            }
        }

        async function onLeadSubmit(data: Record<string, unknown>) {
            try {
                isLoadingSubmitLead.value = true
                isLeadSubmitted.value = false
                submitLeadError.value = undefined
                await apiClient.value.submitLead({
                    agentId: agentIdRef.value,
                    chatId: localChatId.value,
                    ...data,
                })
                isLeadSubmitted.value = true
            } catch {
                submitLeadError.value = te('message.leadSubmitError')
                    ? t('message.leadSubmitError')
                    : 'Failed to submit lead'
            } finally {
                isLoadingSubmitLead.value = false
            }
        }

        async function renameChatTitle(chatId: string, title: string) {
            const updated = await apiClient.value.updateChatTitle(
                agentIdRef.value,
                chatId,
                title,
            )
            conversations.value = conversations.value.map((c) =>
                c.id === chatId ? { ...c, title: updated.title } : c,
            )
        }

        async function deleteChat(chatId: string) {
            await apiClient.value.deleteChat(agentIdRef.value, chatId)
            conversations.value = conversations.value.filter(
                (c) => c.id !== chatId,
            )
            if (localChatId.value === chatId) {
                await startNewChat()
            }
        }

        function navigate(view: ChatbotView) {
            activeView.value = view
        }

        function focusInput() {
            navigate('chat')
            inputFocusRequest.value++
        }

        // === Identity switches ===
        /**
         * Last userId the chat state was bound to. Starts undefined: adopting
         * the first identity of the page must not reset anything, because in
         * every same-user flow (anonymous re-auth, restored session) the
         * persisted chatId belongs to it, while `loadChat` leaves foreign
         * chats empty on a fresh page load anyway.
         */
        let boundUserId: string | undefined

        /**
         * GET the current chat as the new user, retrying once on failure: a
         * transient network error must not be mistaken for "not owned" and
         * wipe a conversation the user actually keeps. After the retry it
         * still fails closed (privacy wins over continuity).
         */
        function isChatReadable(chatId: string): Promise<boolean> {
            const probe = () =>
                apiClient.value
                    .getChat(agentIdRef.value, chatId)
                    .then((data) => data !== null)
            return probe().catch(() => probe().catch(() => false))
        }

        /**
         * The persisted chatId and the in-memory messages survive auth
         * changes, so when the session identity changes (host login via
         * external token, sign-out followed by a fresh anonymous user) the
         * previous user's conversation would keep showing. The server
         * enforces chat ownership, so ask it whether the current chat is
         * readable by the new user: it is only when it was transferred along
         * (in-widget sign-in links the anonymous user and moves their chats).
         * Otherwise drop the previous user's state and start a fresh chat.
         */
        watch(userId, async (newUserId) => {
            if (!newUserId || newUserId === boundUserId) {
                // Sign-out (undefined) keeps the state: it is either followed
                // by a new identity (anonymous fallback / next login), which
                // resets here, or no chat is rendered at all.
                return
            }
            const previousUserId = boundUserId
            boundUserId = newUserId
            if (previousUserId === undefined) {
                return
            }
            const verifiedChatId = localChatId.value
            // Reasoning chats are never persisted server-side, so the probe
            // could only fail: skip it and reset directly.
            const isChatOwnedByNewUser = isReasoningAgent.value
                ? false
                : await isChatReadable(verifiedChatId)
            if (
                userId.value !== newUserId ||
                localChatId.value !== verifiedChatId
            ) {
                // Superseded: another identity change or a chat rotation won
                return
            }
            if (isChatOwnedByNewUser) {
                // Same conversation, new owner (anonymous → linked account):
                // keep the messages, refresh the user-scoped list.
                loadConversations(agentIdRef.value)
                return
            }
            conversations.value = []
            messageFeedbacks.value = []
            chatFiles.value = []
            serverRunningChatIds.value = []
            backgroundTurns.value = []
            answeredInBackground.value = []
            backgroundQuestions.value = {}
            // Rotating the chatId recreates the chat (clearing the
            // previous user's messages) and re-triggers `initialize` in the
            // components watching it, which reloads the user-scoped data.
            await startNewChat()
            input.value = ''
        })

        return {
            // Configuration
            apiClient,
            baseUrl,
            basePath,
            agentId: agentIdRef,
            name,
            agentType,
            userId,
            agentInterface,
            agentModel,
            agentTools,
            headers,
            revisedAnswers,
            model,
            actionsInput,
            actions,
            agentFileUpload,
            agentVoice,
            fallbackModels,
            gatewayOptions,
            externalContext,
            agentHostTools,
            hostToolRegistry,
            hostToolsPending,
            agentPageAccess,
            pageToolRegistry,
            pageFormsProvider,
            pageAction,
            hostStepTitles,
            pageControlActive,
            isWorking,
            stopHostActions,
            voicePageTools,
            cancelVoicePageCalls,
            pageNoticeLevel,
            closePageNotice,
            hostConfirmation,
            respondHostConfirmation,
            pageAccessAvailable,
            pageAccessOff,
            setPageAccessOff,
            pagePointer,
            pagePointerStart,
            pagePointing,
            pagePointerAvailable,
            systemTheme,
            hideWelcomeMessage,
            isDark,
            // Chat state
            chat,
            messages,
            conversations,
            isLoadingConversations,
            hasMoreConversations,
            isLoadingMoreConversations,
            chatFiles,
            isLoadingChatFiles,
            input,
            pendingAttachments,
            isAttachmentLimitReached,
            fileLimitError,
            localChatId,
            isConversationBlocked,
            inputMessagePlaceholder,
            chatError,
            // Background turns and the queue (spec resumable-chat-streams, F2)
            runningChatIds,
            backgroundAnswers,
            openBackgroundAnswer,
            dismissBackgroundAnswer,
            canQueue,
            queuedMessages,
            removeQueuedMessage,
            editQueuedMessage,
            // Navigation
            activeView,
            inputFocusRequest,
            // Feedback state
            messageFeedbacks,
            feedbackDialogMessage,
            isFeedbackSubmitting,
            isFeedbackSubmitted,
            feedbackSubmitError,
            // Lead state
            isLeadSubmitted,
            isLoadingSubmitLead,
            submitLeadError,
            // Actions
            buildDeviceContext,
            initialize,
            loadConversations,
            loadMoreConversations,
            searchConversations,
            loadChatFiles,
            refreshMessages,
            appendMessage,
            getChatFileUrl,
            getChatFilePreviewUrl,
            deleteChatFile,
            handleSubmit,
            sendMessage,
            stopGeneration,
            startNewChat,
            regenerate,
            addToolOutput,
            addToolApprovalResponse,
            handleFileSelect,
            clearFileLimitError,
            clearPendingAttachments,
            onUpvote,
            onDownvote,
            onFeedback,
            onFeedbackSubmit,
            onLeadSubmit,
            navigate,
            focusInput,
            renameChatTitle,
            deleteChat,
        }
    })()
}
